/**
 * Шапка карточки упражнения: миниатюра, название, вторая строка, вес.
 *
 * Одна на все экраны, где упражнение стоит карточкой: правка тренировки,
 * «Сегодня», тренировка, «История», прогресс, цель, состав программы. Тап по
 * миниатюре раскрывает технику прямо в этой карточке, под шапкой. Уводить
 * человека на другой экран ради картинки нельзя: в карточке правки может лежать
 * несохранённая схема, на тренировке — текущий подход.
 *
 * ОДИН ПРОСМОТР НА ЭКРАН. Открыт ли просмотр, помнит экран, а не шапка: открыв
 * технику во второй карточке, человек закрывает первую. Если та стоит выше,
 * страница сдвигается на её высоту — а в iOS WebKit нет scroll anchoring, и
 * нажатая миниатюра уехала бы вверх из-под пальца. Поэтому переключение идёт
 * через `keepInPlace`.
 *
 * АТРИБУЦИЯ. Рисунки под CC BY-SA: автор и лицензия стоят в той же карточке,
 * что и миниатюра, — короткой строкой под шапкой. Пока просмотр открыт, строка
 * не нужна: в просмотре полная атрибуция со ссылками на источники. Экран, где
 * миниатюр в одном невысоком блоке несколько (день программы), ставит одну
 * строку на блок сам и передаёт `credit={false}`.
 */

import { useId, useRef, type ReactNode } from 'react'
import { flushSync } from 'react-dom'

import { ExerciseThumb, ThumbGap, hasPeek, type ExerciseRef } from './ExerciseThumb'
import TechniqueCard, { TechniqueCredit } from './TechniqueCard'

/**
 * Применить изменение раскладки так, чтобы `anchor` остался на том же месте
 * экрана.
 *
 * Изменение применяется синхронно (`flushSync`), затем страница докручивается
 * ровно на сдвиг якоря. `scrollIntoView` здесь не годится: он ставит элемент к
 * верхнему краю, под фиксированную полоску синхронизации и далеко от пальца.
 */
export function keepInPlace(anchor: Element | null, update: () => void): void {
  const before = anchor?.getBoundingClientRect().top
  flushSync(update)
  if (!anchor || before === undefined || !anchor.isConnected) return
  const shift = anchor.getBoundingClientRect().top - before
  if (Math.abs(shift) > 1) window.scrollBy(0, shift)
}

export function ExerciseHead({
  exercise,
  open,
  onToggle,
  title,
  sub,
  aside,
  repeat = false,
  credit = true,
  inlinePeek = true,
}: {
  exercise: ExerciseRef
  open: boolean
  onToggle: () => void
  title: ReactNode
  /** Вторая строка слева: схема, «в прошлый раз». */
  sub?: ReactNode
  /** Справа во второй строке: вес или кнопка. */
  aside?: ReactNode
  /** Повтор того же движения подряд (серия в 5/3/1): место под миниатюру без кнопки. */
  repeat?: boolean
  /** Короткая атрибуция под шапкой. `false` — экран ставит её сам. */
  credit?: boolean
  /** `false` — технику раскладывает экран (после серии, а не внутри неё). */
  inlinePeek?: boolean
}) {
  const peekId = useId()
  const rootRef = useRef<HTMLDivElement>(null)
  const peekable = hasPeek(exercise)
  const flat = !peekable && !repeat
  // Название без второй строки встаёт по центру миниатюры, а не к её верху.
  const line = sub === undefined && aside === undefined

  function close(): void {
    const button = rootRef.current?.querySelector<HTMLButtonElement>('.xp-thumb')
    onToggle()
    // Кнопка «Закрыть» исчезает вместе с просмотром — фокус возвращается туда,
    // откуда просмотр открыли, а не в начало страницы.
    button?.focus({ preventScroll: true })
  }

  return (
    <>
      <div
        ref={rootRef}
        className={`xh${flat ? ' xh-flat' : ''}${line ? ' xh-line' : ''}`}
      >
        {repeat ? (
          <ThumbGap />
        ) : (
          // Ключ по движению: на тренировке шапка переживает переход к
          // следующему упражнению, и без ключа под новым названием висел бы
          // кадр прошлого, пока грузится новый.
          <ExerciseThumb
            key={exercise.id}
            exercise={exercise}
            expanded={open}
            controls={open && inlinePeek ? peekId : undefined}
            onPeek={(button) => keepInPlace(button, onToggle)}
          />
        )}
        <div className="xh-title">{title}</div>
        {sub !== undefined && <div className="xh-sub">{sub}</div>}
        {aside !== undefined && <div className="xh-aside">{aside}</div>}
      </div>

      {open && inlinePeek && !repeat && peekable && (
        <TechniqueCard id={peekId} exerciseId={exercise.id} name={exercise.name} onClose={close} />
      )}

      {credit && !open && !repeat && (
        <TechniqueCredit exerciseIds={[exercise.id]} withSources={false} compact />
      )}
    </>
  )
}
